/**
 * Proyectos.tsx — la cartera de servicios: lo que Backlog reporta a reconocer,
 * lo que cuesta y en qué se ocupa el equipo, en el mes de trabajo.
 *
 * ── ▶▶ EL RECONOCIMIENTO LO MANDA BACKLOG, Books NO calcula ───────────────
 *
 * Backlog envía el MONTO a reconocer de cada proyecto (0116). Books no lo deriva
 * de precio × porcentaje ni de ninguna otra cuenta: procesa ese monto tal como
 * llegó. Por eso acá no se declara avance — se MUESTRA lo reportado y, con el
 * botón de reconocer, se GENERA el asiento del pendiente, exacto.
 *
 * `objetivo` es lo reportado acumulado, `ya_reconocido` lo ya asentado, y el
 * `ajuste` (= objetivo − ya_reconocido) es lo que el asiento va a escribir.
 *
 * ── Las horas y el costo también son de Backlog ───────────────────────────
 *
 * Salen de la rentabilidad y la utilización (horas_de_proyecto), no de
 * avance_previsto —que lee la tabla manual, ya sin uso—. Procedencia rotulada.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { anioYMesDeHoy } from '../nucleo/hoy.ts';
import { esCero, sumar, type Importe } from '../nucleo/dinero.ts';
import { puedeRegistrar, useMesDeTrabajo, useSesion } from '../nucleo/sesion.tsx';
import { nombreDe, useCuentasDeMovimiento } from '../nucleo/catalogo.tsx';
import {
  agregarProyecto, cerrarProyecto, reconocerAvance, traerContrapartes, traerProyectos,
  traerMonedasOperables, FalloDeApi,
  type AvancePrevisto, type Contraparte,
  type CuadreDeProyectos, type MonedaDeProyecto,
  type MonedaOperable, type RentabilidadDeProyecto, type UtilizacionDePersona,
} from '../datos/libros.ts';

const MESES = Array.from({ length: 12 }, (_, i) => i + 1);
const enHoras = (minutos: number) => (minutos / 60).toFixed(2);

export function Proyectos() {
  const { t, monto, idioma } = useIdioma();
  const { pedir, sesion } = useSesion();
  const hojas = useCuentasDeMovimiento();

  const cuentasDePasivo = hojas.filter((c) => c.tipo === 'liability');
  const cuentasDeActivo = hojas.filter((c) => c.tipo === 'asset');
  const cuentasDeIngreso = hojas.filter((c) => c.tipo === 'income');
  const escribe = puedeRegistrar(sesion);

  // `hoy` es el calendario, para la fecha de inicio de un proyecto nuevo (un
  // hecho real). El SELECTOR de mes parte del mes de trabajo (0150).
  const hoy = anioYMesDeHoy();
  const trabajo = useMesDeTrabajo();
  const [anio, setAnio] = useState(trabajo.anio);
  const [mes, setMes] = useState(trabajo.mes);

  const [previo, setPrevio] = useState<readonly AvancePrevisto[]>([]);
  const [monedasProy, setMonedasProy] = useState<readonly MonedaDeProyecto[]>([]);
  const [monedasOperables, setMonedasOperables] = useState<readonly MonedaOperable[]>([]);
  const [cuadre, setCuadre] = useState<CuadreDeProyectos | null>(null);
  const [rentabilidad, setRentabilidad] = useState<readonly RentabilidadDeProyecto[]>([]);
  const [utilizacion, setUtilizacion] = useState<readonly UtilizacionDePersona[]>([]);
  const [clientes, setClientes] = useState<readonly Contraparte[]>([]);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [hecho, setHecho] = useState<string | null>(null);
  const [abrirForm, setAbrirForm] = useState(false);

  const [nuevo, setNuevo] = useState({
    codigo: '', nombre: '', numero: '', contraparte: '', cuentaDiferida: '', cuentaIngreso: '',
    precio: '', moneda: '', inicio: `${hoy.anio}-${String(hoy.mes).padStart(2, '0')}-01`,
  });

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const r = await traerProyectos(pedir, anio, mes);
      setPrevio(r.previo);
      setMonedasProy(r.monedas);
      setCuadre(r.cuadre);
      setRentabilidad(r.rentabilidad);
      setUtilizacion(r.utilizacion);
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    }
  }, [pedir, anio, mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const [cs, ms] = await Promise.all([
          traerContrapartes(pedir, 'cliente'),
          traerMonedasOperables(pedir),
        ]);
        if (vivo) { setClientes(cs); setMonedasOperables(ms); }
      } catch { /* del formulario de alta: si falla, el reconocimiento sigue */ }
    })();
    return () => { vivo = false; };
  }, [pedir]);

  async function correr(fn: () => Promise<unknown>) {
    setOcupado(true);
    setFallo(null);
    try {
      await fn();
      await cargar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  const moneda = previo[0]?.moneda ?? cuadre?.moneda ?? 'CRC';
  const monedaContrato = (cod: string) =>
    monedasProy.find((m) => m.codigo === cod)?.moneda ?? moneda;
  const rentaDe = (cod: string) => rentabilidad.find((r) => r.proyecto === cod) ?? null;

  // ▶ Lo pendiente de reconocer: el ajuste (objetivo reportado − ya asentado) de
  // cada proyecto. Es lo que el botón va a asentar. El ajuste va en la moneda
  // funcional (una sola por empresa), así que `sumar` nunca cruza monedas acá.
  const pendientes: Importe[] = previo
    .filter((p) => !esCero({ valor: p.ajuste, moneda }))
    .map((p) => ({ valor: p.ajuste, moneda }));
  const hayPendiente = pendientes.length > 0;
  const totalPendiente = hayPendiente ? sumar(pendientes) : null;

  // ▶ El margen del mes SUMA dinero entre proyectos: por `dinero.sumar`, que da
  // null si las monedas difieren (§6.1) — entonces «varias monedas», no una resta
  // de colones y dólares. Las horas no son dinero: enteros.
  const margenesValidos: Importe[] = rentabilidad
    .filter((r) => !r.sin_costo && !r.costo_otra_moneda)
    .map((r) => ({ valor: r.margen, moneda: r.moneda }));
  const margenMes = margenesValidos.length > 0 ? sumar(margenesValidos) : null;
  const minutosMes = utilizacion.reduce((a, u) => a + u.minutos, 0);
  const activos = previo.filter((p) => !p.cerrado).length;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t('pry.title')}</h1>
          <p className="page-head__sub">{t('pry.sub')}</p>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}
      {hecho && (
        <div className="alert alert--ok" style={{ marginBottom: 'var(--s6)' }}>
          <span>{hecho}</span>
        </div>
      )}

      {/* ── El mes, los KPIs y reconocer — el estado de un vistazo y la acción ── */}
      <section className="card" style={{ marginBottom: 'var(--s6)' }}>
        <div className="card__head">
          <h3>{t('pry.monthTitle')}</h3>
          <span className="spacer" />
          <span className="small muted">{t('pry.fromBacklog')}</span>
        </div>

        <div className="row" style={{ padding: '0 var(--s7)', gap: 'var(--s4)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>{t('pry.period')}
            <select
              className="select" value={`${anio}-${mes}`}
              onChange={(e) => {
                const [a, m] = e.target.value.split('-');
                setAnio(Number(a));
                setMes(Number(m));
                setHecho(null);
              }}
            >
              {MESES.map((m) => (
                <option key={m} value={`${anio}-${m}`}>
                  {`${anio}-${String(m).padStart(2, '0')}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid--kpi" style={{ padding: 'var(--s5) var(--s7) var(--s6)' }}>
          <div>
            <div className="kpi__label">{t('pry.kpiPending')}</div>
            <div className="kpi__value">
              {totalPendiente ? monto({ valor: totalPendiente.valor, moneda: totalPendiente.moneda }) : '—'}
            </div>
          </div>
          <div>
            <div className="kpi__label">{t('pry.kpiMargin')}</div>
            <div className="kpi__value">
              {margenMes ? monto({ valor: margenMes.valor, moneda: margenMes.moneda })
                : margenesValidos.length === 0 ? '—' : t('pry.manyCurrencies')}
            </div>
          </div>
          <div>
            <div className="kpi__label">{t('pry.kpiHours')}</div>
            <div className="kpi__value">{enHoras(minutosMes)} h</div>
          </div>
          <div>
            <div className="kpi__label">{t('pry.kpiActive')}</div>
            <div className="kpi__value">{activos}</div>
          </div>
        </div>

        {/* ▶ Reconocer DE PRIMERA: procesa el monto que Backlog reportó y GENERA el
            asiento del pendiente, exacto. Sólo aparece cuando hay algo pendiente. */}
        {escribe && (
          <div className="row" style={{ padding: '0 var(--s7) var(--s7)', gap: 'var(--s5)', flexWrap: 'wrap', alignItems: 'center' }}>
            {hayPendiente && (
              <button
                className="btn" type="button" disabled={ocupado}
                onClick={() => void correr(async () => {
                  const r = await reconocerAvance(pedir, anio, mes);
                  setHecho(r.asiento === null
                    ? t('pry.doneNone')
                    : t('pry.doneOk', {
                      importe: monto({ valor: r.total, moneda }),
                      asiento: String(r.asiento),
                    }));
                })}
              >
                {t('pry.recognise')}
              </button>
            )}
            <span className="small muted">
              {hayPendiente ? t('pry.recogniseNote') : t('pry.nothingPending')}
            </span>
          </div>
        )}
      </section>

      {/* ── El cuadre, sólo cuando hay algo que conciliar ─────────────────── */}
      {cuadre && cuadre.proyectos > 0 && (!cuadre.cuadra || cuadre.en_negativo > 0) && (
        <section className="alert alert--warn" style={{ marginBottom: 'var(--s6)', display: 'block' }}>
          {!cuadre.cuadra && (
            <div className="small">
              {t('pry.mismatch', {
                objetivo: monto({ valor: cuadre.objetivo, moneda }),
                reconocido: monto({ valor: cuadre.reconocido, moneda }),
              })}
            </div>
          )}
          {cuadre.en_negativo > 0 && (
            <div className="small" style={{ marginTop: 'var(--s3)' }}>
              {t('pry.negative', { cuantos: String(cuadre.en_negativo) })}
            </div>
          )}
        </section>
      )}

      {/* ── Una TARJETA por proyecto, de LECTURA: lo reportado, lo reconocido,
          lo pendiente, y la rentabilidad. Sin controles: el reconocimiento lo
          manda Backlog y se asienta con el botón de arriba. ── */}
      {previo.length === 0 ? (
        <section className="card" style={{ marginBottom: 'var(--s6)' }}>
          <p className="small muted" style={{ padding: 'var(--s6) var(--s7)' }}>
            {t('pry.noneAtAll')}
          </p>
        </section>
      ) : previo.map((p) => {
        const r = rentaDe(p.proyecto);
        const mc = monedaContrato(p.proyecto);
        const incompleto = !r || r.sin_costo || r.costo_otra_moneda;
        const margenNeg = r ? Number(r.margen) < 0 : false;
        const pendiente = !esCero({ valor: p.ajuste, moneda: p.moneda });
        return (
          <section key={p.proyecto} className="card" style={{ marginBottom: 'var(--s5)' }}>
            <div style={{ padding: 'var(--s6) var(--s7) 0', display: 'flex', alignItems: 'baseline', gap: 'var(--s4)', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500 }}>{p.nombre}</span>
              <span className="mono small muted">{p.proyecto}</span>
              <span className="small muted">· {p.contraparte}</span>
              {p.cerrado && <span className="chip chip--soft">{t('pry.closed')}</span>}
              <span className="spacer" />
              {r && !incompleto && r.margen_pct !== null && (
                <span className={margenNeg ? 'chip chip--danger' : 'chip chip--ok'}>
                  {t('pry.marginChip', { pct: r.margen_pct })}
                </span>
              )}
            </div>

            <div className="grid grid--kpi" style={{ padding: 'var(--s5) var(--s7)' }}>
              <div>
                <div className="kpi__label">{t('pry.reportedToRecognise')}</div>
                <div className="mono">{monto({ valor: p.objetivo, moneda: p.moneda })}</div>
              </div>
              <div>
                <div className="kpi__label">{t('pry.recognised')}</div>
                <div className="mono">{monto({ valor: p.ya_reconocido, moneda: p.moneda })}</div>
              </div>
              <div>
                <div className="kpi__label">{t('pry.pending')}</div>
                <div className="mono" style={{ color: pendiente ? 'var(--acc-ink)' : undefined }}>
                  {monto({ valor: p.ajuste, moneda: p.moneda })}
                </div>
              </div>
              <div>
                <div className="kpi__label">{t('pry.colCost')}</div>
                <div className="mono">
                  {r && !incompleto ? monto({ valor: r.costo, moneda: r.moneda })
                    : <span className="muted">—</span>}
                </div>
                {r?.sin_costo && <div className="small muted">{t('pry.noCostYet')}</div>}
                {r?.costo_otra_moneda && <div className="small muted">{t('pry.costOtherCurrency')}</div>}
              </div>
              <div>
                <div className="kpi__label">{t('pry.colMargin')}</div>
                <div className="mono" style={{ color: incompleto ? undefined : margenNeg ? 'var(--danger)' : 'var(--ok)' }}>
                  {r && !incompleto ? monto({ valor: r.margen, moneda: r.moneda })
                    : <span className="muted">—</span>}
                </div>
              </div>
            </div>

            <div className="row" style={{ padding: '0 var(--s7) var(--s6)', gap: 'var(--s4)', alignItems: 'center' }}>
              <span className="small muted">
                {t('pry.cardFoot', {
                  horas: r ? r.horas : '0.00',
                  precio: monto({ valor: p.precio, moneda: mc }),
                })}
              </span>
              <span className="spacer" />
              {/* ▶ Cerrar es lo único de ciclo de vida que queda acá: un proyecto
                  terminado deja de reconocer. No es un cálculo ni un dato de
                  declaración; por eso sobrevivió al retiro de lo manual. */}
              {escribe && !p.cerrado && (
                <button
                  className="btn btn--ghost btn--sm" type="button" disabled={ocupado}
                  onClick={() => void correr(() => cerrarProyecto(
                    pedir, p.proyecto, `${anio}-${String(mes).padStart(2, '0')}-01`))}
                >
                  {t('pry.close')}
                </button>
              )}
            </div>
          </section>
        );
      })}

      {/* ── El equipo · utilización del mes (lo que reporta Backlog, 0151) ── */}
      {utilizacion.length > 0 && (
        <section className="card" style={{ marginBottom: 'var(--s6)' }}>
          <div className="card__head">
            <h3>{t('pry.teamTitle')}</h3>
            <span className="spacer" />
            <span className="small muted">{t('pry.fromBacklog')}</span>
          </div>
          <div style={{ padding: '0 var(--s7) var(--s6)' }}>
            {utilizacion.map((u) => {
              const pctFact = u.minutos > 0
                ? Math.round((u.minutos_facturable / u.minutos) * 100) : 0;
              return (
                <div key={u.persona} className="row" style={{ gap: 'var(--s4)', alignItems: 'center', padding: 'var(--s3) 0' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>{u.persona}</div>
                    <div style={{ height: 5, borderRadius: 999, background: 'var(--border)', overflow: 'hidden', marginTop: 4 }}>
                      <div style={{ width: `${pctFact}%`, height: '100%', background: 'var(--acc)' }} />
                    </div>
                  </div>
                  <div className="mono small" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {enHoras(u.minutos)} h
                    <div className="muted">{t('pry.billablePct', { pct: String(pctFact) })}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="small muted" style={{ padding: '0 var(--s7) var(--s7)' }}>
            {t('pry.teamWhy')}
          </p>
        </section>
      )}

      {/* ── El alta de un proyecto (secundario) ───────────────────────────── */}
      {escribe && (
        <section className="card">
          <div className="card__head">
            <h3>{t('pry.addTitle')}</h3>
            <button className="btn btn--ghost btn--sm" type="button"
              onClick={() => setAbrirForm((v) => !v)}>
              {abrirForm ? t('common.cancel') : t('pry.add')}
            </button>
          </div>
          {abrirForm && (
            <div className="stack" style={{ padding: '0 var(--s7) var(--s7)' }}>
              <p className="small muted">{t('pry.addHelp')}</p>
              <div className="grid grid--form">
                <label>{t('pry.code')}
                  <input className="input input--mono" value={nuevo.codigo}
                    onChange={(e) => setNuevo({ ...nuevo, codigo: e.target.value })} />
                </label>
                <label>{t('pry.name')}
                  <input className="input" value={nuevo.nombre}
                    onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
                </label>
                <label>{t('pry.number')} <span className="small muted">· {t('pry.numberHint')}</span>
                  <input className="input input--mono" inputMode="numeric" value={nuevo.numero}
                    onChange={(e) => setNuevo({ ...nuevo, numero: e.target.value.replace(/\D/g, '') })} />
                </label>
                <label>{t('pry.customer')}
                  <select className="select" value={nuevo.contraparte}
                    onChange={(e) => setNuevo({ ...nuevo, contraparte: e.target.value })}>
                    <option value="">—</option>
                    {clientes.map((c) => (
                      <option key={c.codigo} value={c.codigo ?? ''}>{c.nombre}</option>
                    ))}
                  </select>
                </label>
                <label>{t('pry.accountContract')} <span className="small muted">· {t('pry.contractHint')}</span>
                  <select className="select" value={nuevo.cuentaDiferida}
                    onChange={(e) => setNuevo({ ...nuevo, cuentaDiferida: e.target.value })}>
                    <option value="">—</option>
                    <optgroup label={t('pry.groupDeferred')}>
                      {cuentasDePasivo.map((c) => (
                        <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>
                      ))}
                    </optgroup>
                    <optgroup label={t('pry.groupContractAsset')}>
                      {cuentasDeActivo.map((c) => (
                        <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>
                      ))}
                    </optgroup>
                  </select>
                </label>
                <label>{t('pry.accountIncome')} <span className="small muted">· {t('pry.onlyIncome')}</span>
                  <select className="select" value={nuevo.cuentaIngreso}
                    onChange={(e) => setNuevo({ ...nuevo, cuentaIngreso: e.target.value })}>
                    <option value="">—</option>
                    {cuentasDeIngreso.map((c) => (
                      <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>
                    ))}
                  </select>
                </label>
                <label>{t('pry.price')}
                  <input className="input input--mono" inputMode="decimal" value={nuevo.precio}
                    onChange={(e) => setNuevo({ ...nuevo, precio: e.target.value })} />
                </label>
                {monedasOperables.length > 0 && (
                  <label>{t('pry.currency')}
                    <select className="input" value={nuevo.moneda}
                      onChange={(e) => setNuevo({ ...nuevo, moneda: e.target.value })}>
                      {monedasOperables.map((m) => (
                        <option key={m.moneda} value={m.es_local ? '' : m.moneda}>{m.moneda}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>{t('pry.start')}
                  <input className="input" type="date" value={nuevo.inicio}
                    onChange={(e) => setNuevo({ ...nuevo, inicio: e.target.value })} />
                </label>
              </div>
              <div className="row" style={{ gap: 'var(--s4)' }}>
                <button
                  className="btn" type="button"
                  disabled={ocupado || !nuevo.codigo || !nuevo.nombre || !nuevo.contraparte
                    || !nuevo.cuentaDiferida || !nuevo.cuentaIngreso || !nuevo.precio}
                  onClick={() => void correr(async () => {
                    await agregarProyecto(pedir, { ...nuevo });
                    setAbrirForm(false);
                    setNuevo({ ...nuevo, codigo: '', nombre: '', numero: '', precio: '', moneda: '' });
                  })}
                >
                  {t('pry.save')}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
